import type { Money } from './types';

export const LKR_MINOR_FACTOR = 100;
export const MAX_MONEY_MINOR = Number.MAX_SAFE_INTEGER;
export const MIN_MONEY_MINOR = Number.MIN_SAFE_INTEGER;

const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);

export function moneyFromMajor(value: string | number): Money {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new Error(
        'Numeric major-unit input must be a safe whole number; use a string for decimals',
      );
    }
    return multiplyMoney(value, LKR_MINOR_FACTOR);
  }

  const normalized = value.trim();
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) throw new Error(`Invalid monetary amount: ${normalized}`);

  const sign = match[1] === '-' ? -1n : 1n;
  const whole = BigInt(match[2] ?? '0');
  const fraction = BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  const minor = sign * (whole * 100n + fraction);
  if (minor > MAX_SAFE_BIGINT || minor < -MAX_SAFE_BIGINT) {
    throw new Error('Money exceeds safe integer range');
  }
  return Number(minor);
}

export function moneyToMajor(value: Money): string {
  assertMoney(value);
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  const whole = Math.floor(absolute / LKR_MINOR_FACTOR);
  const fraction = absolute % LKR_MINOR_FACTOR;
  return `${sign}${whole}.${String(fraction).padStart(2, '0')}`;
}

export function formatMoney(value: Money, currency = 'LKR', locale = 'en-LK'): string {
  assertMoney(value);
  const absolute = Math.abs(value);
  const whole = Math.floor(absolute / LKR_MINOR_FACTOR);
  const fraction = absolute % LKR_MINOR_FACTOR;
  const groupedWhole = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(whole);
  const sign = value < 0 ? '-' : '';
  return `${currency} ${sign}${groupedWhole}.${String(fraction).padStart(2, '0')}`;
}

export function assertMoney(value: number, label = 'Money'): asserts value is Money {
  if (!Number.isSafeInteger(value)) {
    throw new Error(`${label} must be a safe integer in minor units`);
  }
}

export function assertNonNegativeMoney(value: number, label = 'Money'): asserts value is Money {
  assertMoney(value, label);
  if (value < 0) throw new Error(`${label} must not be negative`);
}

export function assertPositiveMoney(value: number, label = 'Money'): asserts value is Money {
  assertMoney(value, label);
  if (value <= 0) throw new Error(`${label} must be greater than zero`);
}

export function addMoney(left: Money, right: Money): Money {
  assertMoney(left);
  assertMoney(right);
  const result = left + right;
  assertMoney(result, 'Money result');
  return result;
}

export function subtractMoney(left: Money, right: Money): Money {
  assertMoney(left);
  assertMoney(right);
  const result = left - right;
  assertMoney(result, 'Money result');
  return result;
}

export function multiplyMoney(value: Money, multiplier: number): Money {
  assertMoney(value);
  if (!Number.isSafeInteger(multiplier)) throw new Error('Money multiplier must be a safe integer');
  const result = BigInt(value) * BigInt(multiplier);
  if (result > MAX_SAFE_BIGINT || result < -MAX_SAFE_BIGINT) {
    throw new Error('Money result exceeds safe integer range');
  }
  return Number(result);
}

export function absoluteMoney(value: Money): Money {
  assertMoney(value);
  return Math.abs(value);
}

export function sumMoney(values: readonly Money[]): Money {
  return values.reduce<Money>((sum, value) => addMoney(sum, value), 0);
}

export function ceilDivideMoney(value: Money, divisor: number): Money {
  assertNonNegativeMoney(value);
  if (!Number.isSafeInteger(divisor) || divisor <= 0) {
    throw new Error('Money divisor must be a positive safe integer');
  }
  const quotient = Math.trunc(value / divisor);
  return addMoney(quotient, value % divisor === 0 ? 0 : 1);
}

export function ratioBasisPoints(numerator: Money, denominator: Money): number | null {
  assertMoney(numerator);
  assertMoney(denominator);
  if (denominator === 0) return null;

  const negative = numerator < 0 !== denominator < 0;
  const absoluteNumerator = BigInt(Math.abs(numerator));
  const absoluteDenominator = BigInt(Math.abs(denominator));
  const scaled = absoluteNumerator * 10_000n;
  const rounded = (scaled + absoluteDenominator / 2n) / absoluteDenominator;
  const signed = negative ? -rounded : rounded;
  const result = Number(signed);
  if (!Number.isSafeInteger(result)) throw new Error('Ratio exceeds safe integer range');
  return result;
}
